import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ApiService } from '../../../../services/api/api.service';
import { UserProfileService } from '../../../../services/user/user-profile.service';
import { UtilityService } from '../../../../services/utility.service';
import { CampaignDetailsService } from '../../../../services/campaign/campaign-details.service';
import { RecipientsService } from '../../../../services/campaign/recipients.service';
import { MessagesService, SavedMessages } from '../../../../services/campaign/messages.service';
import { ScheduleService } from '../../../../services/campaign/schedule.service';
import { CampaignStepperService } from '../../../../services/campaign/campaign-stepper.service';
import { IOrganization, ITag } from '../../../../types/contactModule';
import { IEntitySegmentQuery } from '../../../../components/organization/segments/segment-utilities';
import { WizardAudienceService } from './wizard-audience.service';

/**
 * "Duplicate" copies an existing campaign into the create wizard: name,
 * description, goals, recipient groups and message copy. Dates and send
 * times are left empty to be chosen again. Nothing is saved: the copy only
 * becomes a campaign when it is launched from the review step.
 *
 * Any page with a campaign's id and uuid can use it:
 *   duplicator.prefill(campaign).subscribe(() =>
 *     router.navigate(['/organization/campaigns/create-campaign'], { queryParams: { step: 'campaign-details' } }));
 */
@Injectable({ providedIn: 'root' })
export class CampaignDuplicateService {
  private api = inject(ApiService);
  private user = inject(UserProfileService);
  private utility = inject(UtilityService);
  private campaignDetailsService = inject(CampaignDetailsService);
  private recipientsService = inject(RecipientsService);
  private messagesService = inject(MessagesService);
  private scheduleService = inject(ScheduleService);
  private stepper = inject(CampaignStepperService);
  private audience = inject(WizardAudienceService);

  /** Name of the campaign the wizard was filled from, for the wizard header. */
  sourceName: string | null = null;

  /**
   * Segments of the source campaign that could not be matched to a segment in
   * the contacts service (deleted, or stored without a usable id). They are
   * left out, and the wizard asks for them to be chosen again.
   */
  droppedSegments = 0;

  /** Loads the campaign (and its recipient groups) and fills the wizard with it. */
  prefill(campaign: { id: number | string; uuid?: string | null; name?: string | null }): Observable<void> {
    const org = this.user.organizationId;
    // The same two requests the campaign page makes: the campaign, then its groups.
    const details$ = this.api.get<any>(
      `blackkonnectcxpengagement/api/engagement-campaigns/organisations/${org}/${campaign.id}`
    );
    const groups$ = campaign.uuid
      ? this.api
          .get<any>(
            `blackkonnectcxpengagement/api/engagement-campaigns/v1/organisations/${org}/campaign/${campaign.uuid}/receipients`
          )
          .pipe(catchError(() => of(null)))
      : of(null);

    return forkJoin({ details: details$, groups: groups$ }).pipe(
      switchMap(({ details, groups }) =>
        this.resolveSegments(segmentRefs(details ?? {}, groups)).pipe(
          map(({ segments, dropped }) => this.apply(details ?? {}, groups, segments, dropped))
        )
      )
    );
  }

  /** Forget the source, e.g. when the wizard is cancelled or launched. */
  clear() {
    this.sourceName = null;
    this.droppedSegments = 0;
  }

  private apply(details: any, groups: any, segments: IEntitySegmentQuery[], dropped: number) {
    // Start clean: nothing from an earlier, abandoned draft carries over.
    this.campaignDetailsService.reset();
    this.recipientsService.reset();
    this.messagesService.reset();
    this.messagesService.resetSavedMessages();
    this.scheduleService.reset();
    this.stepper.reset();
    this.audience.reset();

    const name = String(details?.name ?? '').trim();
    this.sourceName = name || 'Untitled campaign';
    this.droppedSegments = dropped;

    const goals: string[] = (Array.isArray(details?.engagementObjectives) ? details.engagementObjectives : [])
      .map((objective: any) => String(objective?.name ?? '').trim())
      .filter(Boolean);

    this.campaignDetailsService.campaignDetails = {
      campaignName: name ? `${name} (copy)` : '',
      description: String(details?.description ?? ''),
      goals,
    };

    this.recipientsService.recipients = {
      organizations: this.organizations(details, groups),
      tags: this.tags(details, groups),
      segments,
    };

    const messages: SavedMessages[] = (Array.isArray(details?.engagementMessages) ? details.engagementMessages : [])
      .filter((message: any) => message && (message.content || message.messageSubject))
      // The API returns them as a set; their ids keep the order they were written in.
      .sort((a: any, b: any) => Number(a?.id ?? 0) - Number(b?.id ?? 0))
      .map((message: any) => ({
        id: this.utility.randomId(),
        subject: message.messageSubject ?? '',
        body: message.content ?? '',
        hashtags: [],
        senderId: message.from ?? message.senderId ?? '',
      }));
    this.messagesService.savedMessages = messages;

    // Steps that are already complete can be skipped; dates and sends cannot.
    const draft = this.campaignDetailsService.campaignDetails;
    if (draft.campaignName && draft.description && goals.length) {
      this.stepper.stepCompleted('campaign-details');
    }
    // Only a selection the launch can use counts as done: every group needs the
    // uuid the audience is resolved by and the id the campaign record stores.
    const { organizations, tags } = this.recipientsService.recipients;
    const chosen = [...organizations, ...tags, ...segments] as { id?: unknown; uuid?: string }[];
    const usable = chosen.every((group) => isUuid(group?.uuid) && group?.id != null && group?.id !== '');
    if (chosen.length && usable && !dropped) {
      this.stepper.stepCompleted('recipients');
    }
    if (messages.length && messages.every((message) => message.subject && message.body && message.senderId)) {
      this.stepper.stepCompleted('messages');
    }
  }

  /**
   * Groups from the campaign record carry the contact-service ids the
   * pickers match on; the recipients endpoint fills in any the record lacks.
   */
  private organizations(details: any, groups: any): IOrganization[] {
    const fromRecord = (details?.engagementOrganizations ?? []).map((org: any) => ({
      id: org?.organizationIntId ?? undefined,
      uuid: org?.organizationId ?? undefined,
      name: org?.name ?? '',
    }));
    // The recipients endpoint answers ReceipientGroupInfo: { uuid, name, groupId, description }.
    const fromGroups = (groups?.engagementRecipientCompanies ?? []).map((org: any) => ({
      id: org?.groupId ?? undefined,
      uuid: org?.uuid ?? undefined,
      name: org?.name ?? '',
    }));
    return merge(fromRecord, fromGroups) as unknown as IOrganization[];
  }

  private tags(details: any, groups: any): ITag[] {
    const fromRecord = (details?.engagementRecipientTags ?? []).map((tag: any) => ({
      id: tag?.tagIntId ?? undefined,
      uuid: tag?.tagUuid ?? undefined,
      name: tag?.name ?? '',
      description: null,
    }));
    const fromGroups = (groups?.engagementRecipientTags ?? []).map((tag: any) => ({
      id: tag?.groupId ?? undefined,
      uuid: tag?.uuid ?? undefined,
      name: tag?.name ?? '',
      description: null,
    }));
    return merge(fromRecord, fromGroups) as unknown as ITag[];
  }

  /**
   * Matches the copied segment references to segments in the contacts
   * service, which gives the uuid the audience needs and the id the record
   * stores. Campaign records keep the segment's numeric id (as `segmentId`)
   * and usually no uuid, so a reference alone is not enough to launch with.
   */
  private resolveSegments(refs: SegmentRef[]): Observable<{ segments: IEntitySegmentQuery[]; dropped: number }> {
    if (!refs.length) return of({ segments: [], dropped: 0 });

    const ids = [...new Set(refs.map((ref) => ref.id).filter((id): id is number => id !== undefined))];
    const uuids = [...new Set(refs.map((ref) => ref.uuid).filter((uuid): uuid is string => !!uuid))];
    const lookup = (params: Record<string, any>) =>
      this.api
        .get<any>(`blackkonnectcxpcontacts/api/entity-segments/organisations/${this.user.organizationId}`, params)
        .pipe(map((data) => (Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []) as any[]));

    return forkJoin({
      byId: ids.length ? lookup({ 'id.in': ids, size: ids.length }) : of([] as any[]),
      byUuid: uuids.length ? lookup({ 'uuid.in': uuids, size: uuids.length }) : of([] as any[]),
    }).pipe(
      map(({ byId, byUuid }) => {
        const found = [...byUuid, ...byId];
        const segments: IEntitySegmentQuery[] = [];
        const seen = new Set<string>();
        let dropped = 0;
        for (const ref of refs) {
          const match = found.find(
            (segment) =>
              (ref.uuid && segment?.uuid === ref.uuid) || (ref.id !== undefined && Number(segment?.id) === ref.id)
          );
          if (!match || !isUuid(match.uuid) || match.id == null) {
            dropped++;
            continue;
          }
          if (seen.has(match.uuid)) continue;
          seen.add(match.uuid);
          segments.push({ ...match, entitySegmentQueryFilters: match.entitySegmentQueryFilters ?? [] });
        }
        return { segments, dropped };
      }),
      // Contacts service unreachable: keep only references that are already complete.
      catchError(() => {
        const complete = refs.filter((ref) => ref.id !== undefined && isUuid(ref.uuid));
        const segments = complete.map((ref) => ({
          id: ref.id,
          uuid: ref.uuid,
          name: ref.name,
          description: '',
          entitySegmentQueryFilters: [],
        }));
        return of({ segments, dropped: refs.length - complete.length });
      })
    );
  }
}

interface SegmentRef {
  id?: number;
  uuid?: string;
  name: string;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

/** A whole, positive number from a number or a numeric string; otherwise undefined. */
function toId(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : undefined;
}

/**
 * The segments a campaign was sent to, from its record
 * (EngagementRecipientSegmentDTO: segmentId, segmentIntId, uuid) and from the
 * recipients endpoint (ReceipientGroupInfo: uuid, groupId). Before a campaign
 * sends, that endpoint reports the stored segmentId (a numeric id) as `uuid`,
 * so a non-UUID value there is read as an id. One reference per segment.
 */
function segmentRefs(details: any, groups: any): SegmentRef[] {
  const fromRecord: SegmentRef[] = (details?.engagementRecipientSegments ?? []).map((segment: any) => ({
    id: toId(segment?.segmentIntId) ?? toId(segment?.segmentId),
    uuid: [segment?.uuid, segment?.segmentUuid, segment?.segmentId].find(isUuid),
    name: String(segment?.name ?? ''),
  }));
  const fromGroups: SegmentRef[] = (groups?.engagementRecipientSegments ?? []).map((group: any) => ({
    id: toId(group?.groupId) ?? toId(group?.uuid),
    uuid: isUuid(group?.uuid) ? group.uuid : undefined,
    name: String(group?.name ?? ''),
  }));

  const refs: SegmentRef[] = [];
  for (const ref of [...fromRecord, ...fromGroups]) {
    if (ref.id === undefined && !ref.uuid) continue;
    const known = refs.find(
      (item) => (ref.uuid && item.uuid === ref.uuid) || (ref.id !== undefined && item.id === ref.id)
    );
    if (!known) {
      refs.push({ ...ref });
    } else {
      known.id ??= ref.id;
      known.uuid ??= ref.uuid;
      if (!known.name) known.name = ref.name;
    }
  }
  return refs;
}

/** One entry per uuid; the first list wins, the second fills gaps. */
function merge<T extends { uuid?: string; id?: unknown; name?: string }>(first: T[], second: T[]): T[] {
  const byUuid = new Map<string, T>();
  for (const item of [...first, ...second]) {
    if (!item?.uuid) continue;
    const known = byUuid.get(item.uuid);
    if (!known) {
      byUuid.set(item.uuid, { ...item });
    } else {
      if (known.id === undefined && item.id !== undefined) known.id = item.id;
      if (!known.name && item.name) known.name = item.name;
    }
  }
  return [...byUuid.values()];
}
